import { t } from 'i18next';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as api from 'src/state/api';
import { notificationsManager } from 'src/state/Notifications';
import {
  Actions as SigningRulesAndSignatoriesActions,
  utils as SigningRulesAndSignatoriesUtils,
} from 'src/state/SigningRulesAndSignatories';
import SigningCaseActions from './actions';
import ActionTypes from './types';
import { getGlobal } from '../../utils/getGlobal';

type TFetchContractingParty = ReturnType<
  typeof SigningCaseActions['fetchSigningCase']
>;

function* fetchSigningCase(status: TFetchContractingParty) {
  try {
    yield all([
      put(SigningCaseActions.fetchSigningCaseRequest()),
      put(
        SigningRulesAndSignatoriesActions.fetchSigningRulesAndSignatoriesRequest()
      ),
    ]);

    const [signingCase, signingRulesAndSignatories] = yield all([
      call(api.getSigningCase, status.payload.caseId, status.payload.partyId),
      call(
        api.getSigningRulesAndSignatories,
        status.payload.caseId,
        status.payload.partyId
      ),
    ]);

    const translatedRulesAndSignatories = SigningRulesAndSignatoriesUtils.translateSigningRulesAndSignatories(
      signingRulesAndSignatories.data,
      t
    );

    yield put(SigningCaseActions.fetchSigningCaseSuccess(signingCase.data));
    yield put(
      SigningRulesAndSignatoriesActions.fetchSigningRulesAndSignatoriesSuccess(
        translatedRulesAndSignatories
      )
    );

    const g = getGlobal();
    const isFromMvpApp = g.location.href.includes('/app');
    const isSuccessFromMvpDsp = isFromMvpApp
      ? 'YouHaveSuccessfullySignedTheAgreementInMvpAppText'
      : 'YouHaveSuccessfullySignedTheAgreement';
    const header =
      isFromMvpApp && 'YouHaveSuccessfullySignedTheAgreementInMvpAppHeader';

    const successUpdate = signingCase.data.status === 'Signed';

    if (successUpdate) {
      yield put(
        notificationsManager.addNotification({
          text: isSuccessFromMvpDsp,
          header: header,
          type: 'Success',
        })
      );
    }

    const canceledBySignatoryCase =
      signingCase.data.status === 'CancelledBySignatory';

    if (isFromMvpApp && canceledBySignatoryCase) {
      yield put(
        notificationsManager.addNotification({
          text: 'CanceledExpiredInMvpAppText',
          header: 'CanceledExpiredInMvpAppHeader',
          type: 'Error',
        })
      );
    }

    if (window.location.href.includes('/Archive')) {
      yield put(
        notificationsManager.removeNotification(
          'YouHaveSuccessfullySignedTheAgreement'
        )
      );
    }
  } catch (e) {
    yield put(SigningCaseActions.fetchSigningCaseFailure());
    yield put(
      SigningRulesAndSignatoriesActions.fetchSigningRulesAndSignatoriesFailure()
    );
    yield put(notificationsManager.addNotification(e));
  }
}

type MarkReadProps = ReturnType<typeof SigningCaseActions['documentMarkRead']>;
function* markDocumentRead(props: MarkReadProps) {
  try {
    yield call(api.markRead, props.payload);
    yield put(
      SigningCaseActions.documentMarkReadSuccess({
        documentId: props.payload.documentId,
      })
    );
  } catch (e) {
    return;
  }
}

export function* watchFetchSigningCase() {
  yield takeEvery(ActionTypes.FETCH_SIGNING_CASE, fetchSigningCase);
}

export function* watchMarkDocumentRead() {
  yield takeLatest(ActionTypes.MARK_DOCUMENT_READ, markDocumentRead);
}
